import React, { useState, useEffect } from 'react';
import { Tabs, Tab, Typography } from '@mui/material';
import { NewReleasesOutlined } from '@mui/icons-material';

import { useSelector, useDispatch } from 'react-redux';
import {
  selectChartTab,
  selectIsChartVisible,
  selectOverviewTab,
} from '../../store/ui/ui.selector';
import {
  selectIsClimate,
  selectThemeMarkdownFolderName,
  selectUseHurricaneApi,
} from '../../store/options/options.selector';
import { selectCurrentLocation, selectUseStationData } from '../../store/map/map.selector';
import { changeOverviewTab } from '../../store/ui/ui.slice';

import Section from '../section/section.component';
import MarkdownToJsx from '../markdown-to-jsx/markdown-to-jsx.component';
import OverviewText from '../overview-text/overview-text.component';
import generateLocalText from './text-generators/local-text';

import OverviewBtnGroup from '../overview-btn-group/with-hurricane.overview-btn-group.component';
// import OverviewBtnGroup from '../overview-btn-group/overview-btn-group.component';

import './overview-card.styles.scss';

export default function OverviewCard() {
  const [showBadge, setShowBadge] = useState(true);

  const dispatch = useDispatch();
  const isChartVisible = useSelector(selectIsChartVisible);
  const tab = useSelector(selectOverviewTab);
  const isClimate = useSelector(selectIsClimate);
  const loc = useSelector(selectCurrentLocation);
  const mdFolderName = useSelector(selectThemeMarkdownFolderName);
  const chartTab = useSelector(selectChartTab);
  const useHurricane = useSelector(selectUseHurricaneApi);
  const useStation = useSelector(selectUseStationData);

  useEffect(() => {
    setShowBadge(true);
  }, [loc]);

  const handleTabChange = (e, newTab) => {
    dispatch(changeOverviewTab(newTab));
    if (newTab === 'regional') setShowBadge(false);
  };

  const renderText = () => {
    if (!loc.name && !useStation) {
      return (
        <Typography>
          Please click on a state/county to view detailed data.
        </Typography>
      );
    }

    let results;
    if (tab === 'regional') {
      const CISA = ['NC', 'SC'];
      const GLISA = ['MN', 'WI', 'MI', 'IL', 'IN', 'OH'];
      const MARISA = ['VA', 'WV', 'MD', 'PA', 'DE', 'NJ', 'NY', 'DC'];

      const stateAbbr = loc.state ? loc.state : loc.id;
      let region = 'default';
      if (CISA.includes(stateAbbr)) {
        region = 'CISA';
      } else if (GLISA.includes(stateAbbr)) {
        region = 'GLISA';
      } else if (MARISA.includes(stateAbbr)) {
        region = 'MARISA';
      }
      const regionalOverviewFolderPath = [process.env.PUBLIC_URL, 'markdown', 'regional_overview', mdFolderName].join('/');
      results = {
        title: 'Overview',
        children: <MarkdownToJsx path={`${regionalOverviewFolderPath}/${region}.md`} fallback={`${regionalOverviewFolderPath}/default.md`} />
      };
    } else {
      results = generateLocalText(
        loc,
        isClimate,
        chartTab,
        isChartVisible,
        useHurricane,
        useStation
      );
    }

    return (
      <OverviewText title={results.title}>{results.children}</OverviewText>
    );
  };

  return (
    <Section id='overview-container'>
      <div className='overview-tabs-container'>
        <Tabs
          className='overview-tabs'
          value={tab}
          onChange={handleTabChange}
          variant='fullWidth'
        >
          <Tab
            className={`${
              tab === 'regional' ? 'tab-selected' : 'tab-unselected'
            }`}
            value='regional'
            label={
              <div className='new-content-badge'>
                <div>Regional Overview</div>
                <NewReleasesOutlined
                  id='new-overview'
                  className={showBadge ? 'faded-in' : 'faded-out'}
                />
              </div>
            }
          />
          <Tab
            className={`${tab === 'local' ? 'tab-selected' : 'tab-unselected'}`}
            value='local'
            label={useStation ? 'Station Details' : `${loc.state ? 'County' : 'State'} Details`}
          />
        </Tabs>
        <div className='tabs-divider' />
      </div>

      {renderText()}

      <div className='overview-bottom'>
        <OverviewBtnGroup />
      </div>
    </Section>
  );
}
